import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  Divider,
  Tooltip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import PeopleOutlineRoundedIcon from "@mui/icons-material/PeopleOutlineRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import marketingservice from "../../repository/marketingRepository";

const MarketingAnalytics = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({
    totalVisitors: 0,
    totalUsers: 0,
    totalAppDownloads: 0,
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");

  const [filterSource, setFilterSource] = useState("all");
  const [filterMedium, setFilterMedium] = useState("all");
  const [filterCampaign, setFilterCampaign] = useState("all");
  const [filterDownloaded, setFilterDownloaded] = useState("all");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [selectedAttribution, setSelectedAttribution] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);

  const colors = {
    background: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#191715" : "#FFFFFF",
    input: isDark ? "#151412" : "#FCFBF9",
    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#AAA49C" : "#77716A",
    accent: isDark ? "#D1B99A" : "#806B52",
    accentSoft: isDark ? "rgba(209,185,154,.10)" : "rgba(128,107,82,.08)",
    border: isDark ? "rgba(255,255,255,.08)" : "rgba(50,40,30,.09)",
    success: isDark ? "#8FC6A3" : "#3D8054",
    warning: isDark ? "#D7B77A" : "#A66E18",
    danger: isDark ? "#D99494" : "#B54545",
    blue: isDark ? "#8FB8E8" : "#476F9F",
  };

  const cardSx = {
    borderRadius: "14px",
    backgroundColor: colors.card,
    border: `1px solid ${colors.border}`,
    boxShadow: isDark
      ? "0 8px 30px rgba(0,0,0,.12)"
      : "0 8px 30px rgba(50,40,30,.045)",
  };

  /*
   * ---------------------------------------------
   * FETCH MARKETING DATA
   * ---------------------------------------------
   */

  const fetchMarketingData = async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const token = sessionStorage.getItem("newToken");

      const params = {
        page,
        limit,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (filterSource !== "all") {
        params.utm_source = filterSource;
      }

      if (filterMedium !== "all") {
        params.utm_medium = filterMedium;
      }

      if (filterCampaign !== "all") {
        params.utm_campaign = filterCampaign;
      }

      if (filterDownloaded !== "all") {
        params.appDownloaded = filterDownloaded;
      }

      if (fromDate) {
        params.fromDate = fromDate;
      }

      if (toDate) {
        params.toDate = toDate;
      }

      const response = await marketingservice.getMarketingAtt(token, params);

      if (response.success) {
        setData(response.data || []);

        setSummary({
          totalVisitors: response.data?.summary?.totalVisitors || 0,

          totalUsers: response.data?.summary?.totalUsers || 0,

          totalAppDownloads: response.data?.summary?.totalAppDownloads || 0,
        });

        setPagination(
          response.data?.pagination || {
            page,
            limit,
            total: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        );
      }
    } catch (err) {
      console.error("Marketing analytics error:", err);

      setError(
        err?.response?.data?.message || "Failed to fetch marketing analytics",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /*
   * ---------------------------------------------
   * FETCH
   * ---------------------------------------------
   */

  useEffect(() => {
    fetchMarketingData();
  }, [
    page,
    limit,
    searchTerm,
    filterSource,
    filterMedium,
    filterCampaign,
    filterDownloaded,
    fromDate,
    toDate,
  ]);

  /*
   * ---------------------------------------------
   * RESET PAGE WHEN FILTER CHANGES
   * ---------------------------------------------
   */

  useEffect(() => {
    setPage(1);
  }, [
    searchTerm,
    filterSource,
    filterMedium,
    filterCampaign,
    filterDownloaded,
    fromDate,
    toDate,
  ]);

  /*
   * ---------------------------------------------
   * UNIQUE FILTER VALUES
   * ---------------------------------------------
   */

  const sourceOptions = useMemo(() => {
    const values = data.map((item) => item?.utm_source).filter(Boolean);

    return [...new Set(values)];
  }, [data]);

  const mediumOptions = useMemo(() => {
    const values = data.map((item) => item?.utm_medium).filter(Boolean);

    return [...new Set(values)];
  }, [data]);

  const campaignOptions = useMemo(() => {
    const values = data.map((item) => item?.utm_campaign).filter(Boolean);

    return [...new Set(values)];
  }, [data]);

  /*
   * ---------------------------------------------
   * FORMAT DATE
   * ---------------------------------------------
   */

  const formatDate = (date) => {
    if (!date) return "-";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return date;
    }

    return parsed.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /*
   * ---------------------------------------------
   * SHORT TEXT
   * ---------------------------------------------
   */

  const shortText = (value, length = 25) => {
    if (!value) return "-";

    const stringValue = String(value);

    if (stringValue.length <= length) {
      return stringValue;
    }

    return `${stringValue.substring(0, length)}...`;
  };

  /*
   * ---------------------------------------------
   * DOWNLOAD CHIP
   * ---------------------------------------------
   */

  const DownloadChip = ({ downloaded }) => {
    if (downloaded) {
      return (
        <Chip
          icon={<DownloadRoundedIcon sx={{ fontSize: 15 }} />}
          label="Downloaded"
          size="small"
          sx={{
            height: 27,
            borderRadius: "7px",
            fontSize: 10.5,
            fontWeight: 650,
            color: colors.success,
            backgroundColor: isDark
              ? "rgba(143,198,163,.10)"
              : "rgba(61,128,84,.09)",
            border: `1px solid ${colors.success}22`,
            "& .MuiChip-icon": {
              color: colors.success,
            },
          }}
        />
      );
    }

    return (
      <Chip
        label="Not Downloaded"
        size="small"
        sx={{
          height: 27,
          borderRadius: "7px",
          fontSize: 10.5,
          fontWeight: 650,
          color: colors.secondary,
          backgroundColor: colors.accentSoft,
          border: `1px solid ${colors.border}`,
        }}
      />
    );
  };

  /*
   * ---------------------------------------------
   * OPEN DETAILS
   * ---------------------------------------------
   */

  const openDetails = (item) => {
    setSelectedAttribution(item);
    setDetailsOpen(true);
  };

  /*
   * ---------------------------------------------
   * CLEAR FILTERS
   * ---------------------------------------------
   */

  const clearFilters = () => {
    setSearchTerm("");
    setFilterSource("all");
    setFilterMedium("all");
    setFilterCampaign("all");
    setFilterDownloaded("all");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const hasFilters =
    searchTerm ||
    filterSource !== "all" ||
    filterMedium !== "all" ||
    filterCampaign !== "all" ||
    filterDownloaded !== "all" ||
    fromDate ||
    toDate;

  /*
   * ---------------------------------------------
   * STAT CARD
   * ---------------------------------------------
   */

  const StatCard = ({ title, value, icon, accent = false }) => (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        p: 2.3,
        minHeight: 112,
        display: "flex",
        alignItems: "center",
        gap: 1.8,
        backgroundColor: accent ? colors.accentSoft : colors.card,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: accent
            ? isDark
              ? "rgba(209,185,154,.16)"
              : "rgba(128,107,82,.12)"
            : colors.accentSoft,
          color: colors.accent,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 11,
            color: colors.secondary,
            fontWeight: 500,
            mb: 0.5,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 650,
            lineHeight: 1.1,
            color: colors.text,
          }}
        >
          {Number(value || 0).toLocaleString("en-IN")}
        </Typography>
      </Box>
    </Paper>
  );

  /*
   * ---------------------------------------------
   * MOBILE CARD
   * ---------------------------------------------
   */

  const MobileMarketingCard = ({ item }) => (
    <Card
      elevation={0}
      sx={{
        ...cardSx,
        borderRadius: "12px",
      }}
    >
      <CardContent
        sx={{
          p: 2,
          "&:last-child": {
            pb: 2,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: colors.accent,
                wordBreak: "break-all",
              }}
            >
              {shortText(item?.visitorId, 28)}
            </Typography>

            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 600,
                color: colors.text,
                mt: 0.7,
              }}
            >
              {item?.email ||
                item?.contact ||
                item?.userId ||
                "Anonymous Visitor"}
            </Typography>

            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
                mt: 0.3,
              }}
            >
              {formatDate(item?.first_visit)}
            </Typography>
          </Box>

          <DownloadChip downloaded={item?.appDownloaded} />
        </Box>

        <Divider
          sx={{
            my: 1.5,
            borderColor: colors.border,
          }}
        />

        <Box sx={{ display: "grid", gap: 1.1 }}>
          <Box>
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
                mb: 0.25,
              }}
            >
              Source / Medium
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                color: colors.text,
              }}
            >
              {item?.utm_source || "Direct"}
              {item?.utm_medium ? ` / ${item.utm_medium}` : ""}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
                mb: 0.25,
              }}
            >
              Campaign
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                color: colors.text,
                wordBreak: "break-word",
              }}
            >
              {item?.utm_campaign || "-"}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
                mb: 0.25,
              }}
            >
              Landing Page
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                color: colors.text,
                wordBreak: "break-word",
              }}
            >
              {item?.landing_page || "-"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.secondary,
              }}
            >
              Last Visit
            </Typography>

            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 600,
                color: colors.text,
              }}
            >
              {formatDate(item?.last_visit)}
            </Typography>
          </Box>
        </Box>

        <Button
          fullWidth
          variant="text"
          endIcon={
            <OpenInNewRoundedIcon
              sx={{
                fontSize: "14px !important",
              }}
            />
          }
          onClick={() => openDetails(item)}
          sx={{
            mt: 1.5,
            height: 38,
            borderRadius: "8px",
            color: colors.accent,
            fontSize: 12,
            fontWeight: 650,
            textTransform: "none",
            justifyContent: "space-between",
            px: 1.2,
          }}
        >
          View Attribution
        </Button>
      </CardContent>
    </Card>
  );

  /*
   * ---------------------------------------------
   * LOADING
   * ---------------------------------------------
   */

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 70px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.background,
        }}
      >
        <CircularProgress
          size={30}
          sx={{
            color: colors.accent,
          }}
        />
      </Box>
    );
  }

  /*
   * ---------------------------------------------
   * MAIN
   * ---------------------------------------------
   */

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        backgroundColor: colors.background,
        color: colors.text,
        p: {
          xs: 1.5,
          sm: 2.5,
          md: 4,
        },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
        }}
      >
        {/* HEADER */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
              }}
            >
              <CampaignOutlinedIcon
                sx={{
                  fontSize: 24,
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: {
                    xs: 23,
                    sm: 28,
                    md: 30,
                  },
                  fontWeight: 600,
                  lineHeight: 1.1,
                  color: colors.text,
                }}
              >
                Marketing Analytics
              </Typography>

              <Typography
                sx={{
                  fontSize: {
                    xs: 11.5,
                    sm: 13,
                  },
                  color: colors.secondary,
                  mt: 0.5,
                }}
              >
                Track visitors, campaigns and app conversions
              </Typography>
            </Box>
          </Box>

          <Tooltip title="Refresh analytics">
            <span>
              <IconButton
                onClick={() => fetchMarketingData(true)}
                disabled={refreshing}
                sx={{
                  width: 42,
                  height: 42,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "10px",
                  color: colors.text,
                  backgroundColor: colors.card,
                  "&:hover": {
                    backgroundColor: colors.accentSoft,
                  },
                }}
              >
                {refreshing ? (
                  <CircularProgress
                    size={18}
                    sx={{
                      color: colors.accent,
                    }}
                  />
                ) : (
                  <RefreshRoundedIcon
                    sx={{
                      fontSize: 20,
                    }}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* STAT CARDS */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "repeat(2,1fr)",
              md: "repeat(3,1fr)",
            },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <StatCard
            title="Total Visitors"
            value={summary.totalVisitors}
            accent
            icon={
              <LanguageRoundedIcon
                sx={{
                  fontSize: 21,
                }}
              />
            }
          />

          <StatCard
            title="Registered Users"
            value={summary.totalUsers}
            icon={
              <PeopleOutlineRoundedIcon
                sx={{
                  fontSize: 21,
                }}
              />
            }
          />

          <StatCard
            title="App Downloads"
            value={summary.totalAppDownloads}
            icon={
              <DownloadRoundedIcon
                sx={{
                  fontSize: 21,
                }}
              />
            }
          />
        </Box>

        {/* FILTERS */}

        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: {
              xs: 1.5,
              sm: 2,
            },
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "2fr repeat(4, 1fr)",
              },
              gap: 1.2,
            }}
          >
            {/* SEARCH */}

            <TextField
              fullWidth
              size="small"
              placeholder="Search visitor, user, email, campaign..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 43,
                  borderRadius: "9px",
                  backgroundColor: colors.input,
                  color: colors.text,

                  "& fieldset": {
                    borderColor: colors.border,
                  },

                  "&:hover fieldset": {
                    borderColor: colors.accent,
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: colors.accent,
                  },
                },

                "& .MuiInputBase-input::placeholder": {
                  color: colors.secondary,
                  opacity: 0.8,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon
                      sx={{
                        fontSize: 20,
                        color: colors.secondary,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            {/* SOURCE */}

            <FormControl size="small" fullWidth>
              <InputLabel
                sx={{
                  color: colors.secondary,
                }}
              >
                Source
              </InputLabel>

              <Select
                value={filterSource}
                label="Source"
                onChange={(e) => setFilterSource(e.target.value)}
                sx={{
                  height: 43,
                  borderRadius: "9px",
                  backgroundColor: colors.input,
                  color: colors.text,

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },

                  "& .MuiSvgIcon-root": {
                    color: colors.secondary,
                  },
                }}
              >
                <MenuItem value="all">All Sources</MenuItem>

                {sourceOptions.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* MEDIUM */}

            <FormControl size="small" fullWidth>
              <InputLabel
                sx={{
                  color: colors.secondary,
                }}
              >
                Medium
              </InputLabel>

              <Select
                value={filterMedium}
                label="Medium"
                onChange={(e) => setFilterMedium(e.target.value)}
                sx={{
                  height: 43,
                  borderRadius: "9px",
                  backgroundColor: colors.input,
                  color: colors.text,

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                }}
              >
                <MenuItem value="all">All Mediums</MenuItem>

                {mediumOptions.map((medium) => (
                  <MenuItem key={medium} value={medium}>
                    {medium}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* CAMPAIGN */}

            <FormControl size="small" fullWidth>
              <InputLabel
                sx={{
                  color: colors.secondary,
                }}
              >
                Campaign
              </InputLabel>

              <Select
                value={filterCampaign}
                label="Campaign"
                onChange={(e) => setFilterCampaign(e.target.value)}
                sx={{
                  height: 43,
                  borderRadius: "9px",
                  backgroundColor: colors.input,
                  color: colors.text,

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                }}
              >
                <MenuItem value="all">All Campaigns</MenuItem>

                {campaignOptions.map((campaign) => (
                  <MenuItem key={campaign} value={campaign}>
                    {campaign}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* DOWNLOAD */}

            <FormControl size="small" fullWidth>
              <InputLabel
                sx={{
                  color: colors.secondary,
                }}
              >
                App
              </InputLabel>

              <Select
                value={filterDownloaded}
                label="App"
                onChange={(e) => setFilterDownloaded(e.target.value)}
                sx={{
                  height: 43,
                  borderRadius: "9px",
                  backgroundColor: colors.input,
                  color: colors.text,

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                }}
              >
                <MenuItem value="all">All Users</MenuItem>

                <MenuItem value="true">Downloaded</MenuItem>

                <MenuItem value="false">Not Downloaded</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* DATE FILTER */}

          <Box
            sx={{
              display: "flex",
              gap: 1.2,
              mt: 1.5,
              flexDirection: {
                xs: "column",
                sm: "row",
              },
            }}
          >
            <TextField
              type="date"
              size="small"
              label="From Date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  height: 43,
                  borderRadius: "9px",
                  backgroundColor: colors.input,
                  color: colors.text,

                  "& fieldset": {
                    borderColor: colors.border,
                  },

                  "&:hover fieldset": {
                    borderColor: colors.accent,
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: colors.accent,
                  },
                },

                "& .MuiInputLabel-root": {
                  color: colors.secondary,
                },
              }}
            />

            <TextField
              type="date"
              size="small"
              label="To Date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  height: 43,
                  borderRadius: "9px",
                  backgroundColor: colors.input,
                  color: colors.text,

                  "& fieldset": {
                    borderColor: colors.border,
                  },

                  "&:hover fieldset": {
                    borderColor: colors.accent,
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: colors.accent,
                  },
                },

                "& .MuiInputLabel-root": {
                  color: colors.secondary,
                },
              }}
            />

            {hasFilters && (
              <Button
                onClick={clearFilters}
                sx={{
                  minWidth: {
                    xs: "100%",
                    sm: 120,
                  },
                  height: 43,
                  color: colors.accent,
                  textTransform: "none",
                  fontSize: 12,
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1.5,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: 11.5,
                color: colors.secondary,
              }}
            >
              Showing {data.length.toLocaleString("en-IN")} of{" "}
              {(pagination.total || 0).toLocaleString("en-IN")} visitors
            </Typography>

            <Typography
              sx={{
                fontSize: 11.5,
                color: colors.secondary,
              }}
            >
              Page {pagination.page || 1} of {pagination.totalPages || 1}
            </Typography>
          </Box>
        </Paper>

        {/* DATA */}

        {isMobile ? (
          <Stack spacing={1.5}>
            {data.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  ...cardSx,
                  p: 5,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 650,
                    color: colors.text,
                  }}
                >
                  No marketing data found
                </Typography>

                <Typography
                  sx={{
                    fontSize: 12,
                    color: colors.secondary,
                    mt: 0.5,
                  }}
                >
                  Try changing your filters.
                </Typography>
              </Paper>
            ) : (
              data.map((item) => (
                <MobileMarketingCard
                  key={item?._id || item?.visitorId}
                  item={item}
                />
              ))
            )}
          </Stack>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              ...cardSx,
              maxHeight: "calc(100vh - 420px)",
              minHeight: 350,
              overflow: "auto",

              "&::-webkit-scrollbar": {
                width: 7,
                height: 7,
              },

              "&::-webkit-scrollbar-thumb": {
                backgroundColor: isDark ? "#3A3631" : "#D4CEC5",
                borderRadius: 10,
              },
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "Visitor",
                    "User",
                    "Source",
                    "Campaign",
                    "Landing Page",
                    "App",
                    "Last Visit",
                    "",
                  ].map((title, index) => (
                    <TableCell
                      key={title || index}
                      sx={{
                        backgroundColor: isDark ? "#1D1A18" : "#F3EFE9",
                        color: colors.secondary,
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        borderBottom: `1px solid ${colors.border}`,
                        py: 1.8,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{
                        borderBottom: 0,
                        textAlign: "center",
                        py: 8,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 650,
                          color: colors.text,
                        }}
                      >
                        No marketing data found
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 12,
                          color: colors.secondary,
                          mt: 0.5,
                        }}
                      >
                        Try changing your filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((item) => (
                    <TableRow
                      key={item?._id || item?.visitorId}
                      hover
                      onClick={() => openDetails(item)}
                      sx={{
                        cursor: "pointer",

                        "& td": {
                          borderBottom: `1px solid ${colors.border}`,
                        },

                        "&:hover": {
                          backgroundColor: colors.accentSoft,
                        },
                      }}
                    >
                      {/* VISITOR */}

                      <TableCell
                        sx={{
                          py: 1.8,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            color: colors.accent,
                            maxWidth: 150,
                            wordBreak: "break-all",
                          }}
                        >
                          {shortText(item?.visitorId, 22)}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 9.5,
                            color: colors.secondary,
                            mt: 0.3,
                          }}
                        >
                          {formatDate(item?.first_visit)}
                        </Typography>
                      </TableCell>

                      {/* USER */}

                      <TableCell
                        sx={{
                          py: 1.8,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: colors.text,
                          }}
                        >
                          {item?.userId
                            ? shortText(item.userId, 18)
                            : "Anonymous"}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 10,
                            color: colors.secondary,
                            mt: 0.25,
                            maxWidth: 160,
                            wordBreak: "break-word",
                          }}
                        >
                          {item?.email || item?.contact || "-"}
                        </Typography>
                      </TableCell>

                      {/* SOURCE */}

                      <TableCell
                        sx={{
                          py: 1.8,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: colors.text,
                          }}
                        >
                          {item?.utm_source || "Direct"}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 10.5,
                            color: colors.secondary,
                            mt: 0.25,
                          }}
                        >
                          {item?.utm_medium || "-"}
                        </Typography>
                      </TableCell>

                      {/* CAMPAIGN */}

                      <TableCell
                        sx={{
                          py: 1.8,
                          maxWidth: 180,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            color: colors.text,
                            wordBreak: "break-word",
                          }}
                        >
                          {item?.utm_campaign || "-"}
                        </Typography>

                        {item?.utm_content && (
                          <Typography
                            sx={{
                              fontSize: 9.5,
                              color: colors.secondary,
                              mt: 0.3,
                            }}
                          >
                            {shortText(item.utm_content, 25)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* LANDING PAGE */}

                      <TableCell
                        sx={{
                          py: 1.8,
                          maxWidth: 220,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: colors.secondary,
                            lineHeight: 1.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            wordBreak: "break-word",
                          }}
                        >
                          {item?.landing_page || "-"}
                        </Typography>
                      </TableCell>

                      {/* APP */}

                      <TableCell
                        sx={{
                          py: 1.8,
                        }}
                      >
                        <DownloadChip downloaded={item?.appDownloaded} />
                      </TableCell>

                      {/* LAST VISIT */}

                      <TableCell
                        sx={{
                          py: 1.8,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: colors.text,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatDate(item?.last_visit)}
                        </Typography>
                      </TableCell>

                      {/* ACTION */}

                      <TableCell
                        sx={{
                          py: 1.8,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetails(item);
                          }}
                          sx={{
                            width: 32,
                            height: 32,
                            color: colors.secondary,
                            border: `1px solid ${colors.border}`,

                            "&:hover": {
                              color: colors.accent,
                              backgroundColor: colors.accentSoft,
                            },
                          }}
                        >
                          <OpenInNewRoundedIcon
                            sx={{
                              fontSize: 14,
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* PAGINATION */}

        {pagination.totalPages > 1 && (
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              mt: 1.5,
              p: 1.2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Button
              disabled={!pagination.hasPreviousPage}
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              sx={{
                color: colors.accent,
                textTransform: "none",
                fontSize: 12,
              }}
            >
              Previous
            </Button>

            <Typography
              sx={{
                fontSize: 11.5,
                color: colors.secondary,
              }}
            >
              {pagination.page} / {pagination.totalPages}
            </Typography>

            <Button
              disabled={!pagination.hasNextPage}
              onClick={() => setPage((current) => current + 1)}
              sx={{
                color: colors.accent,
                textTransform: "none",
                fontSize: 12,
              }}
            >
              Next
            </Button>
          </Paper>
        )}
      </Box>

      {/* DETAILS DIALOG */}

      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: colors.card,
            color: colors.text,
            borderRadius: "14px",
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: colors.text,
            fontSize: 18,
            fontWeight: 650,
          }}
        >
          Marketing Attribution
          <IconButton
            onClick={() => setDetailsOpen(false)}
            sx={{
              color: colors.secondary,
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {selectedAttribution && (
            <Stack spacing={2}>
              <DetailSection
                title="Visitor"
                values={[
                  ["Visitor ID", selectedAttribution.visitorId],
                  ["User ID", selectedAttribution.userId],
                  ["Email", selectedAttribution.email],
                  ["Contact", selectedAttribution.contact],
                ]}
                colors={colors}
              />

              <Divider
                sx={{
                  borderColor: colors.border,
                }}
              />

              <DetailSection
                title="UTM Attribution"
                values={[
                  ["Source", selectedAttribution.utm_source],
                  ["Medium", selectedAttribution.utm_medium],
                  ["Campaign", selectedAttribution.utm_campaign],
                  ["Term", selectedAttribution.utm_term],
                  ["Content", selectedAttribution.utm_content],
                ]}
                colors={colors}
              />

              <Divider
                sx={{
                  borderColor: colors.border,
                }}
              />

              <DetailSection
                title="Ad Attribution"
                values={[
                  ["GCLID", selectedAttribution.gclid],
                  ["FBCLID", selectedAttribution.fbclid],
                ]}
                colors={colors}
              />

              <Divider
                sx={{
                  borderColor: colors.border,
                }}
              />

              <DetailSection
                title="Visit"
                values={[
                  ["Landing Page", selectedAttribution.landing_page],
                  ["First Visit", formatDate(selectedAttribution.first_visit)],
                  ["Last Visit", formatDate(selectedAttribution.last_visit)],
                ]}
                colors={colors}
              />

              <Divider
                sx={{
                  borderColor: colors.border,
                }}
              />

              <DetailSection
                title="App"
                values={[
                  [
                    "Downloaded",
                    selectedAttribution.appDownloaded ? "Yes" : "No",
                  ],
                  [
                    "Download At",
                    selectedAttribution.appDownloadAt
                      ? formatDate(selectedAttribution.appDownloadAt)
                      : "-",
                  ],
                ]}
                colors={colors}
              />
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* ERROR */}

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() => setError("")}
          severity="error"
          variant="filled"
          sx={{
            borderRadius: "10px",
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/*
 * ---------------------------------------------
 * DETAIL SECTION
 * ---------------------------------------------
 */

const DetailSection = ({ title, values, colors }) => (
  <Box>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "1px",
        textTransform: "uppercase",
        color: colors.accent,
        mb: 1.2,
      }}
    >
      {title}
    </Typography>

    <Box
      sx={{
        display: "grid",
        gap: 1,
      }}
    >
      {values.map(([label, value]) => (
        <Box
          key={label}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "110px 1fr",
              sm: "140px 1fr",
            },
            gap: 1,
            alignItems: "start",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              color: colors.secondary,
            }}
          >
            {label}
          </Typography>

          <Typography
            sx={{
              fontSize: 12,
              color: colors.text,
              wordBreak: "break-word",
            }}
          >
            {value || "-"}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

export default MarketingAnalytics;
